/**
 * Sección Avisos: lista global de avisos + crear aviso eligiendo la campaña.
 * Los avisos se pueden crear independientemente de la campaña (que pudo crearse
 * sola). Cada aviso enlaza a su detalle para editar y agregar placements.
 */
import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Check, Megaphone, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  media, AD_TYPE_LABELS, type AdType, type AdPriority,
} from "@/lib/api";
import { useActiveClient } from "@/hooks/use-active-client";

export const Route = createFileRoute("/_auth/media/ads")({
  validateSearch: (s: Record<string, unknown>): { campaign?: number } => {
    const c = s.campaign != null ? Number(s.campaign) : undefined;
    return c !== undefined && !Number.isNaN(c) ? { campaign: c } : {};
  },
  component: MediaAds,
});

const PRIORITIES: AdPriority[] = ["baja", "normal", "alta", "urgente"];

function MediaAds() {
  const { campaign: campaignParam } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeClientId } = useActiveClient();
  const [showForm, setShowForm] = useState(campaignParam != null);

  const campaignsQ = useQuery({
    queryKey: ["media", "campaigns"],
    queryFn: () => media.listCampaigns({ limit: 200 }),
  });
  const clientsQ = useQuery({
    queryKey: ["media", "clients", "active"],
    queryFn: () => media.listClients({ is_active: true, limit: 200 }),
  });
  const categoriesQ = useQuery({
    queryKey: ["media", "ad-categories"],
    queryFn: () => media.listAdCategories({ is_active: true }),
  });
  const adsQ = useQuery({
    queryKey: ["media", "ads", "all", activeClientId],
    queryFn: () => media.listAds({ limit: 500, client_id: activeClientId ?? undefined }),
  });

  const campaignName = (id: number) =>
    (campaignsQ.data ?? []).find(c => c.id === id)?.name ?? `Campaña #${id}`;

  // Con "Todos los clientes" cada fila muestra a qué cliente pertenece (vía su campaña).
  const clientTag = (campaignId: number): string | null => {
    if (activeClientId != null) return null;
    const camp = (campaignsQ.data ?? []).find(c => c.id === campaignId);
    if (!camp) return null;
    return (clientsQ.data ?? []).find(c => c.id === camp.client_id)?.name ?? `Cliente #${camp.client_id}`;
  };

  const [form, setForm] = useState<{
    client_id: string; campaign_id: string; title: string; ad_type: AdType;
    category_id: string; priority: AdPriority; summary: string;
  }>({
    client_id: "", campaign_id: campaignParam ? String(campaignParam) : "",
    title: "", ad_type: "aviso_corte", category_id: "", priority: "normal", summary: "",
  });

  // Lanzado desde una campaña (?campaign=): deriva y bloquea el cliente.
  const lockedClient = campaignParam != null;

  // Cliente activo del topbar: pre-selecciona el cliente del form mientras esté cerrado.
  useEffect(() => {
    if (showForm || lockedClient) return;
    setForm(f => ({
      ...f,
      client_id: activeClientId != null ? String(activeClientId) : "",
      campaign_id: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId]);
  useEffect(() => {
    if (campaignParam == null) return;
    const camp = (campaignsQ.data ?? []).find(c => c.id === campaignParam);
    if (camp) {
      setForm(f => ({ ...f, client_id: String(camp.client_id), campaign_id: String(camp.id) }));
    }
  }, [campaignParam, campaignsQ.data]);

  const filteredCampaigns = form.client_id
    ? (campaignsQ.data ?? []).filter(c => String(c.client_id) === form.client_id)
    : [];

  const create = useMutation({
    mutationFn: () => media.createAd({
      campaign_id: Number(form.campaign_id),
      title: form.title,
      ad_type: form.ad_type,
      category_id: form.category_id ? Number(form.category_id) : undefined,
      priority: form.priority,
      summary: form.summary || undefined,
    }),
    onSuccess: (ad) => {
      qc.invalidateQueries({ queryKey: ["media", "ads"] });
      toast.success(`Aviso ${ad.code} creado`);
      navigate({ to: "/media/ads/$adId", params: { adId: String(ad.id) } });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    if (!form.campaign_id) { toast.error("Selecciona una campaña"); return; }
    if (!form.title) { toast.error("Título requerido"); return; }
    create.mutate();
  }

  const ads = adsQ.data ?? [];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-ink-500">{ads.length} avisos</div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            <Plus size={14} /> Nuevo aviso
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-4 border-brand-200">
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cliente *</Label>
                <Select value={form.client_id} disabled={lockedClient}
                  onValueChange={(v) => setForm({ ...form, client_id: v, campaign_id: "" })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                  <SelectContent>
                    {(clientsQ.data ?? []).map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}<span className="text-ink-400"> ({c.code})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!(clientsQ.data ?? []).length && !clientsQ.isLoading && (
                  <p className="text-[10px] text-ink-400 mt-1">No hay clientes. Crea uno primero.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Campaña *</Label>
                <Select value={form.campaign_id} disabled={!form.client_id}
                  onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder={form.client_id ? "Seleccionar campaña..." : "Primero selecciona un cliente"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCampaigns.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}{c.code ? <span className="text-ink-400"> ({c.code})</span> : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.client_id && !filteredCampaigns.length && (
                  <p className="text-[10px] text-ink-400 mt-1">Este cliente no tiene campañas. Crea una primero.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Tipo de aviso *</Label>
                <Select value={form.ad_type} onValueChange={(v) => setForm({ ...form, ad_type: v as AdType })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AD_TYPE_LABELS).map(([k, lbl]) => (
                      <SelectItem key={k} value={k}>{lbl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Título *</Label>
                <Input value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Corte programado zona Arequipa Metropolitana" />
              </div>
              <div>
                <Label className="text-xs">Categoría</Label>
                <Select value={form.category_id || "none"}
                  onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin categoría</SelectItem>
                    {(categoriesQ.data ?? []).map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v as AdPriority })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Resumen / contenido</Label>
                <Textarea rows={2} value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="Texto del aviso a publicar..." />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X size={14} /> Cancelar
              </Button>
              <Button onClick={submit} disabled={create.isPending || !form.client_id || !form.campaign_id || !form.title}>
                <Check size={14} /> Crear aviso
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {adsQ.isLoading ? <Skeleton className="h-40" /> : (
        <div className="space-y-2">
          {ads.map(a => (
            <Link key={a.id} to="/media/ads/$adId" params={{ adId: String(a.id) }}
              className="block">
              <Card className="hover:shadow-md transition-shadow hover:border-brand-300">
                <CardContent className="py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
                    <Megaphone size={16} className="text-brand-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{a.title}</span>
                      <Badge variant="outline" className="font-mono text-[10px]">{a.code}</Badge>
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5 flex items-center gap-2">
                      <span>{AD_TYPE_LABELS[a.ad_type as AdType] ?? a.ad_type}</span>
                      <span>· {campaignName(a.campaign_id)}</span>
                      {clientTag(a.campaign_id) && (
                        <Badge variant="outline" className="text-[10px] text-brand-600 border-brand-300">
                          {clientTag(a.campaign_id)}
                        </Badge>
                      )}
                      {a.status === "descartado" && (
                        <Badge className="text-[10px] bg-slate-200 text-slate-700">Descartado</Badge>
                      )}
                      {a.priority && a.priority !== "normal" && (
                        <Badge variant="outline" className="text-[10px] capitalize">{a.priority}</Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-ink-400 shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
          {!ads.length && (
            <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              No hay avisos. Crea el primero con "Nuevo aviso".
            </div>
          )}
        </div>
      )}
    </>
  );
}
